import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import { ActivityEventType, trackActivity } from "../utilities/activityLog";
import { resolveScreenIdentity, UNMATCHED_ROUTE } from "../utilities/screenIdentity";

/**
 * Emissão de eventos de atividade já ligada à tela atual.
 *
 * Devolve uma função que as telas chamam nos HANDLERS de interação. Não chame de dentro de
 * `load()`: cada tela de relatório chama `load` no mount e a cada push do SignalR via
 * `useLiveRefresh`, então instrumentar ali produziria centenas de `filter_apply` falsos por
 * hora para alguém que só deixou a tela aberta.
 *
 * ```tsx
 * const track = useActivityTracking();
 *
 * const handleApply = (): void => {
 *   if (!pendingPeriod) return;
 *   track(ActivityEventType.FilterApply, describePeriod(pendingPeriod));
 *   void load(pendingPeriod, shiftId);
 * };
 * ```
 */
export const useActivityTracking = (): ((
  eventType: ActivityEventType,
  payload?: Record<string, unknown> | null,
) => void) => {
  const { pathname } = useLocation();
  const identity = useMemo(() => resolveScreenIdentity(pathname), [pathname]);

  return useCallback(
    (eventType: ActivityEventType, payload?: Record<string, unknown> | null): void => {
      // Rota não declarada em `screenIdentity`: descarta em vez de gravar identificador cru.
      if (identity.routePattern === UNMATCHED_ROUTE) return;

      trackActivity({
        eventType,
        screenKey: identity.screenKey,
        routePattern: identity.routePattern,
        payload,
      });
    },
    [identity],
  );
};

/**
 * Igual ao `useActivityTracking`, mas COALESCE disparos seguidos: só emite depois de um período
 * de silêncio, e com o último payload recebido.
 *
 * Serve à navegação por dia (linha do tempo, produção horária), onde clicar cinco vezes na seta
 * é UMA intenção — "quero ver aquele dia" — e não cinco. Sem isso o log registraria os quatro
 * dias intermediários, que o usuário nem chegou a ler.
 *
 * Molde do timer: `hooks/useLiveRefresh.ts:39-45`, que já usa `setTimeout` manual (o
 * `ts-debounce` está no package.json mas não é importado em nenhum lugar de `src/` — não é o
 * padrão da casa).
 */
export const useCoalescedActivityTracking = (
  delayMs = 1_200,
): ((eventType: ActivityEventType, payload?: Record<string, unknown> | null) => void) => {
  const track = useActivityTracking();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Limpa o timer no unmount para não emitir depois de a tela sair.
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  return useCallback(
    (eventType: ActivityEventType, payload?: Record<string, unknown> | null): void => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        track(eventType, payload);
      }, delayMs);
    },
    [track, delayMs],
  );
};

/**
 * Registra a entrada em tela a cada mudança de rota. Montado UMA vez, no `AppLayout`, que é por
 * onde toda navegação autenticada passa — daí não haver instrumentação espalhada por tela.
 *
 * A guarda de idempotência não é defensiva à toa: o StrictMode do React 18 executa os efeitos
 * duas vezes em desenvolvimento, e sem ela todo `screen_view` sairia duplicado — o que
 * inviabilizaria conferir a feature justamente no ambiente onde ela é validada.
 */
export const useScreenViewTracking = (): void => {
  const { pathname } = useLocation();
  const lastTracked = useRef<string | null>(null);

  useEffect(() => {
    if (lastTracked.current === pathname) return;
    lastTracked.current = pathname;

    const { screenKey, routePattern } = resolveScreenIdentity(pathname);
    if (routePattern === UNMATCHED_ROUTE) return;

    trackActivity({
      eventType: ActivityEventType.ScreenView,
      screenKey,
      routePattern,
    });
  }, [pathname]);
};

export default useActivityTracking;
